import React, {
  createContext,
  useContext,
  ChangeEvent,
  useState,
  KeyboardEvent
} from 'react';
import { ColumnDef } from '@tanstack/react-table';
import AdvanceTable from 'components/base/AdvanceTable';
import AdvanceTableFooter from 'components/base/AdvanceTableFooter';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import useAdvanceTable from 'hooks/useAdvanceTable';
import RevealDropdown from 'components/base/RevealDropdown';
import { Dropdown, Modal, Button, Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faEdit, faPlus } from '@fortawesome/free-solid-svg-icons';
import SearchBox from 'components/common/SearchBox';
import DatePicker from 'components/base/DatePicker';
import {
  createBXPTimeCondition,
  updateBXPTimeCondition,
  deleteBXPTimeCondition,
  BXPTimeCondition
} from 'services/hvaservicecampaign.service';

interface TimeConditionsTableProps {
  timeConditions: BXPTimeCondition[];
  onTimeConditionsChange: () => Promise<void>;
}

// Función auxiliar para formatear fechas
const formatDate = (date: Date | null): string => {
  if (!date) return 'Null';
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

const formatDateForBackend = (date: Date | null): string | null => {
  if (!date) return null;
  // Obtenemos el año, mes y día en la zona horaria local
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const TimeConditionsChangeContext = createContext<
  (() => Promise<void>) | undefined
>(undefined);

const useTimeConditionsChange = () => {
  const context = useContext(TimeConditionsChangeContext);
  if (context === undefined) {
    throw new Error(
      'useTimeConditionsChange must be used within a TimeConditionsChangeProvider'
    );
  }
  return context;
};

type TimeConditionActionCellProps = {
  timeCondition: BXPTimeCondition;
};

// eslint-disable-next-line
const TimeConditionActionCell = ({ timeCondition }: TimeConditionActionCellProps) => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  // eslint-disable-next-line
  const [selectedTimeCondition, setSelectedTimeCondition] = useState<BXPTimeCondition | null>(null);
  const onTimeConditionsChange = useTimeConditionsChange();

  const handleCloseDeleteModal = () => setShowDeleteModal(false);
  const handleShowDeleteModal = () => setShowDeleteModal(true);

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setSelectedTimeCondition(null);
  };

  const handleShowEditModal = () => {
    setSelectedTimeCondition({
      ...timeCondition,
      // eslint-disable-next-line
      startdate: timeCondition.startdate ? new Date(timeCondition.startdate) : null,
      enddate: timeCondition.enddate ? new Date(timeCondition.enddate) : null
    });
    setShowEditModal(true);
  };

  const handleConfirmRemove = async () => {
    try {
      await deleteBXPTimeCondition(timeCondition.id);
      console.log(
        `Time Condition with ID: ${timeCondition.id} deleted successfully`
      );
      await onTimeConditionsChange();
    } catch (error) {
      console.error(
        `Error deleting Time Condition with ID: ${timeCondition.id}`,
        error
      );
    } finally {
      handleCloseDeleteModal();
    }
  };

  const handleDateChange = (name: string) => (dates: Date[]) => {
    const [date] = dates;
    setSelectedTimeCondition(prev => (prev ? { ...prev, [name]: date } : null));
  };

  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    let newValue: string | boolean = value;

    if (type === 'checkbox') {
      newValue = (e.target as HTMLInputElement).checked;
    }

    setSelectedTimeCondition(prev =>
      prev ? { ...prev, [name]: newValue } : null
    );
  };

  const handleEditSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (selectedTimeCondition) {
      try {
        const updatedTimeCondition: BXPTimeCondition = {
          ...selectedTimeCondition,
          startdate: formatDateForBackend(
            selectedTimeCondition.startdate as Date | null
          ),
          enddate: formatDateForBackend(
            selectedTimeCondition.enddate as Date | null
          )
        };
        await updateBXPTimeCondition(
          updatedTimeCondition.id,
          updatedTimeCondition
        );
        await onTimeConditionsChange();
        handleCloseEditModal();
      } catch (error) {
        alert('Error updating time condition: ' + error);
      }
    }
  };

  return (
    <>
      <RevealDropdown btnClassName="p-2 pt-1 pb-1 fs-11">
        <Dropdown.Item onClick={handleShowEditModal}>
          <FontAwesomeIcon icon={faEdit} className="fs-8 p-1 pt-0 pb-0" />
          Edit
        </Dropdown.Item>
        <Dropdown.Item onClick={handleShowDeleteModal} className="text-danger">
          <FontAwesomeIcon icon={faTrash} className="fs-8 p-1 pt-0 pb-0" />
          Remove
        </Dropdown.Item>
      </RevealDropdown>

      <Modal
        show={showDeleteModal}
        onHide={handleCloseDeleteModal}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Deletion</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete this Time Condition?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseDeleteModal}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleConfirmRemove}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showEditModal}
        onHide={handleCloseEditModal}
        size="lg"
        centered
      >
        <Form onSubmit={handleEditSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>Update Time Condition</Modal.Title>
          </Modal.Header>
          <Modal.Body className="px-6">
            <Form.Group className="mb-3">
              <Form.Label>Short Name</Form.Label>
              <Form.Control
                type="text"
                name="shortname"
                value={selectedTimeCondition?.shortname || ''}
                onChange={handleInputChange}
                required
                maxLength={32}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Start Date</Form.Label>
              <DatePicker
                name="startdate"
                options={{
                  dateFormat: 'M d, Y',
                  defaultDate: selectedTimeCondition?.startdate || undefined
                }}
                onChange={handleDateChange('startdate')}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>End Date</Form.Label>
              <DatePicker
                name="enddate"
                options={{
                  dateFormat: 'M d, Y',
                  defaultDate: selectedTimeCondition?.enddate || undefined
                }}
                onChange={handleDateChange('enddate')}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <div className="form-check form-switch pb-1 pt-1">
                <label className="form-check-label">
                  <input
                    type="checkbox"
                    name="active"
                    checked={selectedTimeCondition?.active || false}
                    onChange={handleInputChange}
                    className="form-check-input"
                  />
                  Active
                </label>
              </div>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseEditModal}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              Update Time Condition
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
};

export const timeConditionsTableColumns: ColumnDef<BXPTimeCondition>[] = [
  {
    accessorKey: 'id',
    header: 'ID',
    meta: {
      headerProps: { style: { width: '5%' }, className: 'px-1 fs-10' },
      cellProps: { className: 'fw-bold px-1 white-space-nowrap' }
    }
  },
  {
    accessorKey: 'shortname',
    header: 'Short Name',
    meta: {
      headerProps: { style: { width: '10%' }, className: 'px-1 fs-10' },
      cellProps: { className: 'px-1 white-space-nowrap' }
    }
  },
  {
    accessorKey: 'action',
    enableSorting: false,
    header: '',
    cell: ({ row: { original } }) => {
      return <TimeConditionActionCell timeCondition={original} />;
    },
    meta: {
      cellProps: { className: 'text-body-tertiary' }
    }
  },
  {
    accessorKey: 'startdate',
    header: 'Start Date',
    cell: ({ row }) =>
      formatDate(
        row.original.startdate ? new Date(row.original.startdate) : null
      ),
    meta: {
      headerProps: { style: { width: '15%' }, className: 'px-1 fs-10' },
      cellProps: { className: 'px-1' }
    }
  },
  {
    accessorKey: 'enddate',
    header: 'End Date',
    cell: ({ row }) =>
      formatDate(row.original.enddate ? new Date(row.original.enddate) : null),
    meta: {
      headerProps: { style: { width: '15%' }, className: 'px-1 fs-10' },
      cellProps: { className: 'px-1' }
    }
  },
  {
    accessorKey: 'active',
    header: 'Active',
    cell: ({ row }) => (row.original.active ? 'Yes' : 'No'),
    meta: {
      headerProps: { style: { width: '45%' }, className: 'px-1 fs-10' },
      cellProps: { className: 'px-1 white-space-nowrap' }
    }
  }
];

export const BXPTimeConditionsTable: React.FC<TimeConditionsTableProps> = ({
  timeConditions,
  onTimeConditionsChange
}) => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [newTimeCondition, setNewTimeCondition] = useState<BXPTimeCondition>({
    id: 0,
    shortname: '',
    startdate: null,
    enddate: null,
    active: false
  });
  const table = useAdvanceTable({
    data: timeConditions,
    columns: timeConditionsTableColumns,
    pageSize: 10,
    pagination: true,
    sortable: true,
    selection: false
  });

  const handleSearchInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    table.setGlobalFilter(e.target.value || undefined);
  };

  const handleSearchKeyPress = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
    }
  };

  const handleShowAddModal = () => setShowAddModal(true);
  const handleCloseAddModal = () => setShowAddModal(false);

  const handleDateChange = (name: string) => (dates: Date[]) => {
    const [date] = dates;
    setNewTimeCondition(prev => ({ ...prev, [name]: date }));
  };

  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    let newValue: string | boolean = value;

    if (type === 'checkbox') {
      newValue = (e.target as HTMLInputElement).checked;
    }

    setNewTimeCondition(prev => ({ ...prev, [name]: newValue }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      const timeConditionToCreate: BXPTimeCondition = {
        ...newTimeCondition,
        startdate: formatDateForBackend(
          newTimeCondition.startdate as Date | null
        ),
        enddate: formatDateForBackend(newTimeCondition.enddate as Date | null)
      };
      await createBXPTimeCondition(timeConditionToCreate);
      handleCloseAddModal();
      await onTimeConditionsChange();
    } catch (error) {
      console.error('Error creating time condition:', error);
    }
  };

  return (
    <TimeConditionsChangeContext.Provider value={onTimeConditionsChange}>
      <AdvanceTableProvider {...table}>
        <div className="mb-4">
          <div className="d-flex flex-wrap gap-3">
            <SearchBox
              placeholder="Search Time Conditions"
              onChange={handleSearchInputChange}
              onKeyDown={handleSearchKeyPress}
            />
            <div className="ms-auto">
              <Button
                variant="primary"
                className="p-2"
                onClick={handleShowAddModal}
              >
                <FontAwesomeIcon icon={faPlus} className="me-2 fs-8" />
                Add Time Condition
              </Button>
            </div>
          </div>
        </div>
        <div>
          <AdvanceTable
            tableProps={{ className: 'phoenix-table fs-9' }}
            rowClassName="hover-actions-trigger btn-reveal-trigger position-static"
          />
          <AdvanceTableFooter pagination />
        </div>
      </AdvanceTableProvider>

      <Modal
        show={showAddModal}
        onHide={handleCloseAddModal}
        size="lg"
        centered
      >
        <Form onSubmit={handleSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>Add New Time Condition</Modal.Title>
          </Modal.Header>
          <Modal.Body className="px-6">
            <Form.Group className="mb-3">
              <Form.Label>Short Name</Form.Label>
              <Form.Control
                type="text"
                name="shortname"
                value={newTimeCondition.shortname}
                onChange={handleInputChange}
                required
                maxLength={32}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Start Date</Form.Label>
              <DatePicker
                name="startdate"
                options={{
                  dateFormat: 'M d, Y',
                  defaultDate: newTimeCondition.startdate || undefined
                }}
                onChange={handleDateChange('startdate')}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>End Date</Form.Label>
              <DatePicker
                name="enddate"
                options={{
                  dateFormat: 'M d, Y',
                  defaultDate: newTimeCondition.enddate || undefined
                }}
                onChange={handleDateChange('enddate')}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <div className="form-check form-switch pb-1 pt-1">
                <label className="form-check-label">
                  <input
                    type="checkbox"
                    name="active"
                    checked={newTimeCondition.active}
                    onChange={handleInputChange}
                    className="form-check-input"
                  />
                  Active
                </label>
              </div>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseAddModal}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              Add Time Condition
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </TimeConditionsChangeContext.Provider>
  );
};
